import { createSelectorFunctions } from 'auto-zustand-selectors-hook'
import { create } from 'zustand'

/**
 * Field names are prefixed (sameSite / crossSite) because Liferay does not
 * allow duplicate field names within the same structure, even across
 * different nested objects.
 */
export interface IModalGeoContextTexts {
  sameSiteTitle: string
  sameSiteDescription: string
  sameSitePrimaryButtonLabel: string
  sameSiteSecondaryButtonLabel: string
  crossSiteTitle: string
  crossSiteDescription: string
  crossSitePrimaryButtonLabel: string
  crossSiteSecondaryButtonLabel: string
  notSupportedCountryTitle: string
  notSupportedCountryDescription: string
  notSupportedCountryPrimaryButtonLabel: string
  notSupportedCountrySecondaryButtonLabel: string
}

export interface IModalGeoContextProps {
  /** CMS kill-switch: render the modal at all */
  showModal: boolean
  /** Texts for same-site locale mismatch (e.g. EC user on /ar) */
  sameSiteTexts: Pick<
    IModalGeoContextTexts,
    | 'sameSiteTitle'
    | 'sameSiteDescription'
    | 'sameSitePrimaryButtonLabel'
    | 'sameSiteSecondaryButtonLabel'
  >
  /** Texts for cross-site visitors (BR user in DGO / SSLA user in SKY+) */
  crossSiteTexts: Pick<
    IModalGeoContextTexts,
    | 'crossSiteTitle'
    | 'crossSiteDescription'
    | 'crossSitePrimaryButtonLabel'
    | 'crossSiteSecondaryButtonLabel'
  >
  /** Texts for visitors from countries not served by either site (non-closable modal) */
  notSupportedCountryTexts: Pick<
    IModalGeoContextTexts,
    | 'notSupportedCountryTitle'
    | 'notSupportedCountryDescription'
    | 'notSupportedCountryPrimaryButtonLabel'
    | 'notSupportedCountrySecondaryButtonLabel'
  >
  /** Optional: overrides the env var for the cross-site home URL */
  crossSiteHomeUrl: string
}

export interface ModalGeoContextPropsStore extends IModalGeoContextProps {
  setProps: (props: IModalGeoContextProps) => void
}

export const useModalGeoContextPropsStore = createSelectorFunctions(
  create<ModalGeoContextPropsStore>((set) => ({
    showModal: false,
    sameSiteTexts: {
      sameSiteTitle: '',
      sameSiteDescription: '',
      sameSitePrimaryButtonLabel: '',
      sameSiteSecondaryButtonLabel: ''
    },
    crossSiteTexts: {
      crossSiteTitle: '',
      crossSiteDescription: '',
      crossSitePrimaryButtonLabel: '',
      crossSiteSecondaryButtonLabel: ''
    },
    notSupportedCountryTexts: {
      notSupportedCountryTitle: '',
      notSupportedCountryDescription: '',
      notSupportedCountryPrimaryButtonLabel: '',
      notSupportedCountrySecondaryButtonLabel: ''
    },
    crossSiteHomeUrl: '',

    setProps: ({
      showModal,
      sameSiteTexts,
      crossSiteTexts,
      notSupportedCountryTexts,
      crossSiteHomeUrl
    }: IModalGeoContextProps) =>
      set({
        showModal,
        sameSiteTexts,
        crossSiteTexts,
        notSupportedCountryTexts,
        crossSiteHomeUrl
      })
  }))
)
